import React, { useContext, useEffect, useRef, useState } from 'react';
import { p } from '../i18n/useP';
import { ENV_TAG, ICONS as NAV_ICONS } from './Sidebar';
import Logo from './Logo';
import { Calendar, Lock } from 'lucide-react';
import GuideButton from './guide/GuideButton';
import { planningLevel } from '../nav/planningLevel';
import { ClosedChip, ReportingContext } from './ReportingControls';
import './AppTopBar.css';

/* ── Barre supérieure de page ────────────────────────────────────────────────
   Le CLIENT (tenant) vit ici depuis le 2026-08-18 : pastille de deux lettres,
   nom en clair, et sélection quand la personne a plusieurs dossiers. Il était
   auparavant en pastille ronde au pied de la barre de navigation, où le nom
   restait invisible — « chez qui suis-je » est un contexte global, pas une
   entrée de menu.

   Elle porte aussi le CONTEXTE TEMPOREL — exercice et frontière réel/prévision
   (2026-08-18) : lu une fois ici plutôt que répété sur la ligne de titre de
   chaque page.

   PLEINE LARGEUR depuis le 2026-08-21 (alignée sur le proto « grammaire
   d'écran ») : couleur du rail, filet inférieur qui traverse tout — au-dessus
   de la navigation ET du contenu. La MARQUE (wordmark PALESTRE + badge
   d'environnement) ouvre la barre, avant le client ; elle a quitté la tête du
   panneau latéral.

   L'ICÔNE TÉLÉCHARGER referme la barre, à droite de l'exercice (2026-08-21) :
   elle est passée par le pied du rail puis par la rangée d'onglets, et ces deux
   places la faisaient disparaître sur les écrans SANS onglets — précisément les
   récits et synthèses qu'on veut imprimer. Ici, elle complète la phrase que la
   barre énonce déjà : pour ce client, pour cet exercice, exporte. */
export default function AppTopBar({ tenants = [], activeTenant, onTenantChange, onHome,
                                   canManageSettings = false, onGoSettings, onGoClosing = null, settingsActive = false, page = null,
                                   isPlatformAdmin = false,
                                   navCollapsed = false,
                                   onToggleKnowledge = null, knowledgeOpen = false, knowledgeDot = false }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const many = tenants.length > 1;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!boxRef.current || !boxRef.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  /* Marque UNIFIÉE (décision Olivier 2026-08-21) : le symbole rejoint le
     wordmark dans la barre — un seul énoncé, clic = Cockpit. La pastille du
     rail est retirée (elle doublait la marque dans le coin haut-gauche). */
  /* L'environnement (LOCAL, DEV) revient PRÈS de la marque, coloré
     (2026-09-01) : il appartient au chrome, pas au contexte client. */
  const env = ENV_TAG
    ? <span className={`sb-env sb-env--${ENV_TAG.toLowerCase()}`}>{ENV_TAG}</span>
    : null;
  /* Disposition « ligne » (essai 2026-09-16) : la LIGNE DES MODULES prend la
     place de la marque dans ce bloc — c'est la même surface que le menu, à la
     même hauteur, et le nom du module retrouve dessous le centre vertical du
     titre de page. La Sidebar y pose ses icônes par portail : la navigation
     n'a qu'un seul code, où que la ligne s'affiche. Le badge d'environnement
     reste, il appartient au chrome. Panneau replié, la ligne n'aurait que
     48 px : la marque revient. */
  /* La ligne s'affiche dès que la disposition est « ligne » : le panneau ne se
     replie plus dans cette disposition, la bande a donc toujours sa largeur. */
  /* Le CLIENT vit dans le bloc de gauche, à côté du symbole, quand les modules
     tiennent la barre (2026-09-17) : ce bloc a la largeur du menu, et le
     laisser au symbole seul ouvrait cent soixante-dix pixels de vide avant le
     nom du client. Hors de cette disposition, il ouvre la zone de droite,
     comme avant. */
  /* PARAMÈTRES, à droite du client (2026-08-23). Ils étaient en pied de rail,
     avec la déconnexion et l'identité : un réglage de dossier n'est pas une
     action de session, et il se cherchait tout en bas de l'écran. Ici, il
     prolonge la phrase du contexte — « chez ce client, ses réglages ». */
  const settingsBtn = canManageSettings ? (
    <button type="button"
            className={`app-topbar__icon${settingsActive ? ' is-active' : ''}`}
            onClick={onGoSettings} title={p('Paramètres')} aria-label={p('Paramètres')}>
      {NAV_ICONS.settings}
    </button>
  ) : null;

  /* L'AMPOULE Savoirs et le GUIDE, chacun son icône (retour d'Olivier du
     2026-09-17, le soir) : ils avaient été rangés le matin sous un accès
     « Aide » à deux entrées nommées, au motif que deux icônes muettes se
     devinaient. Ils se cherchent moins visibles qu'illisibles : un geste
     qu'on fait plusieurs fois par jour ne se range pas derrière un menu.
     Le point dit que la page ouverte a une fiche ou un geste à proposer. */
  const knowledgeBtn = onToggleKnowledge ? (
    <button type="button"
            className={`app-topbar__icon${knowledgeOpen ? ' is-active' : ''}`}
            onClick={onToggleKnowledge}
            title={p('Savoirs')} aria-label={p('Savoirs')} aria-pressed={knowledgeOpen}>
      {NAV_ICONS.savoirs}
      {knowledgeDot && <span className="app-topbar__dot" aria-hidden="true" />}
    </button>
  ) : null;

  const tenantBox = activeTenant ? (
      <div className="app-tenant" ref={boxRef}>
        <button
          type="button"
          className="app-tenant__btn"
          aria-haspopup={many ? 'menu' : undefined}
          aria-expanded={open}
          onClick={() => many && setOpen((v) => !v)}
          style={many ? undefined : { cursor: 'default' }}
        >
          <span className="app-tenant__name">{activeTenant.name}</span>
        </button>
        {open && (
          <div className="app-tenant__menu" role="menu">
            {tenants.map((tenant) => (
              <button
                key={tenant.id} type="button" role="menuitem"
                className={`app-tenant__item${tenant.id === activeTenant.id ? ' is-active' : ''}`}
                onClick={() => { setOpen(false); onTenantChange && onTenantChange(tenant.id); }}
              >
                {tenant.name}
              </button>
            ))}
          </div>
        )}
      </div>
  ) : null;

  /* LE NIVEAU DE PLANIFICATION DU CLIENT (demande d'Olivier, 2026-09-29) :
     dans la barre des modules, à droite avant l'ampoule, sur toutes les pages.
     Un clic ouvre Paramètres › Planification, où il se choisit. Rien pour un client
     qui n'a ni Finance ni Ventes & Ops : il ne planifie pas. */
  const mods = activeTenant?.enabled_modules || [];
  const reporting = useContext(ReportingContext);
  const lvl = planningLevel(activeTenant);
  /* D'UN CLIC (demande d'Olivier, 2026-09-30) : « Manuel · Pro », le mode
     retenu en gras, comme la bascule d'unité ; un clic passe à l'autre. Sans
     Finance, Pro n'existe pas : le mode se lit, il ne bascule pas. */
  const fin = mods.includes('financials');
  const canToggle = canManageSettings && fin && reporting && reporting.onPlanLevelToggle;
  /* PRO RESTE VISIBLE SANS FINANCE (demande d'Olivier, 2026-09-30) : la
     bascule garde la même forme partout, et Pro se lit grisé, cadenas et
     raison au survol ; il donne envie du module au lieu de disparaître. */
  /* RÉSERVÉ À L'ADMINISTRATEUR PALESTRE (demande d'Olivier, 2026-10-22) : le client ne voit pas le niveau de
     planification dans la barre. */
  const levelChip = isPlatformAdmin && activeTenant && (fin || mods.includes('scenarii')) ? (
    <button type="button" className={`unit-switch app-level-toggle${canToggle ? '' : ' is-static'}`} role="switch"
            aria-checked={lvl === 'pro'} aria-disabled={!canToggle || undefined}
            onClick={canToggle ? reporting.onPlanLevelToggle : undefined}
            title={fin ? p('Niveau de planification') : p('Pro demande le module Finance')}>
      <span className={`unit-switch__lab${lvl === 'pro' ? '' : ' on'}`}>{p('Manuel')}</span>
      <span className="unit-switch__sep" aria-hidden="true">·</span>
      <span className={`unit-switch__lab${lvl === 'pro' ? ' on' : ''}${fin ? '' : ' is-locked'}`}>
        {p('Pro')}{!fin && <Lock size={10} aria-hidden="true" />}
      </span>
    </button>
  ) : null;

  const brand = (
    <div className="app-topbar__brand-wrap">
      {/* LE SYMBOLE reste la marque et ouvre le Cockpit (2026-09-16) ; seul le
          wordmark a cédé sa place aux modules. Le nœud du portail est vide et
          distinct : un portail ajoute ses enfants à la FIN du nœud, la ligne se
          retrouverait après le badge. */}
      <>
        {/* L'ENVIRONNEMENT qualifie l'instance, pas le dossier client : il se
            pose SOUS le symbole (demande d'Olivier, 2026-10-12), sans prendre de
            largeur à la ligne ni déplacer le logo, et reste visible en
            permanence — c'est tout l'intérêt d'un tel badge. */}
        <span className="app-topbar__logo-cell">
          <button type="button" className="app-topbar__brand-btn app-topbar__brand-btn--sym"
                  onClick={onHome} title="Palestre">
            <Logo size={26} />
          </button>
          {env}
        </span>
        {tenantBox}
        {/* PARAMÈTRES du client, contre son nom (2026-09-17, soir) : ils
            avaient disparu de la barre le matin, renvoyés dans le menu du
            client, où l'on ne pensait pas à les chercher. Ils prolongent la
            phrase du contexte, « chez ce client, ses réglages ». */}
        {settingsBtn}
      </>
    </div>
  );
  // Le statut du réel a REJOINT l'exercice à droite (2026-09-16) : « Réel ·
  // juil. · 16.09 · S38 · ‹ 2026 › » se lit comme une seule phrase du temps,
  // là où les deux repères étaient séparés par toute la largeur. À gauche
  // reste « où je suis » (marque, client, réglages) ; à droite, « quand je
  // lis ». Le calendrier atténué devant les flèches dit que l'on choisit
  // l'année lue. (Il a été à gauche du 2026-08-18 au 2026-09-16.)
  /* La bande de gauche doit avoir EXACTEMENT la largeur du panneau de la
     disposition ouverte, sinon son filet de droite ne tombe pas sur celui du
     menu — deux traits verticaux à vingt et quelques pixels l'un de l'autre.
     */
  const cls = `app-topbar app-topbar--modules app-topbar--navwide${navCollapsed ? ' app-topbar--navclosed' : ''}`;
  /* Les MODULES au centre de la barre (2026-09-17) : ils ne tiennent plus dans
     la bande du menu, où cinq icônes serrées épuisaient la place. La Sidebar
     les y pose par portail, avec leur nom — la navigation garde un seul code.
     Les adresses de l'exploitant ferment la barre à droite, après les
     outils : elles s'utilisent tous les jours ici. */
  const modsCenter = <div className="app-topbar__mods" id="lk-modslot" />;
  /* Les deux adresses de l'exploitant, Administration et Atelier, ont chacune
     leur bouton (2026-10-11). Ils sont posés par la Sidebar, qui porte déjà la
     navigation ; la barre ne fournit que l'emplacement. */
  /* La PASTILLE utilisateur ferme la barre (2026-09-17) : la Sidebar y pose son
     bloc d'identité par portail, avec les réglages de la personne. Elle quitte
     donc le pied du menu, qui n'a plus à porter ce qui appartient à la
     session. */
  const userSlot = <div className="app-topbar__user" id="lk-userslot" />;
  const opsSlot = isPlatformAdmin ? <div className="app-topbar__ops" id="lk-opsslot" /> : null;
  if (!activeTenant) {
    return (
      <div className={cls}>
        {brand}
        <div className="app-topbar__rest" />
      </div>
    );
  }

  return (
    <div className={cls}>
      {brand}
      <div className="app-topbar__rest">
      {modsCenter}
      {/* Le niveau, à droite, juste avant l'ampoule (demande d'Olivier,
          2026-09-29) : dans le bloc du client, il écrasait son nom. */}
      {/* LE RÉEL, UNE INFORMATION (2026-09-30) : monté de la bande de la page,
          juste avant le niveau et l'ampoule, en texte nu. */}
      <ClosedChip onEdit={canManageSettings && onGoClosing ? onGoClosing : null} />
      {levelChip}
      {knowledgeBtn}
      {/* Le GUIDE (chemin), après l'ampoule : une place fixe, quelle que soit
          la page ; ses repères s'adaptent à l'écran ouvert. */}
      <GuideButton page={page} />
      {opsSlot}
      {userSlot}
      {/* TÉLÉCHARGER rejoint les outils, après le guide (2026-09-17) : il
          fermait la phrase du temps, à l'autre bout de la barre, alors qu'il
          est de la même nature que les réglages, l'ampoule et le guide. */}
      </div>
    </div>
  );
}
